import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Crown, TrendingUp, FileText, ShoppingCart, CheckCircle, Clock, 
  CreditCard, AlertCircle, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const BondMarketplace = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [bonds, setBonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [selectedBond, setSelectedBond] = useState(null);
  const [units, setUnits] = useState(1);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchBonds();
    initDemoData();
  }, []);

  // Check for payment success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const initDemoData = async () => {
    try {
      await axios.post(`${API}/init-demo-data`);
    } catch (error) {
      // Silent fail - demo data might already exist
    }
  };

  const fetchBonds = async () => {
    try {
      const response = await axios.get(`${API}/bonds`);
      setBonds(response.data);
    } catch (error) {
      console.error('Error fetching bonds:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.info('Payment verification timed out. Check your email for confirmation.');
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API}/bonds/payment-status/${sessionId}`, { 
        headers, 
        withCredentials: true 
      });

      if (response.data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('Payment successful! Your bond subscription is confirmed.');
        fetchBonds();
        navigate('/bonds', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired.');
        navigate('/bonds', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setCheckingPayment(false);
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Please login to subscribe to bonds');
      navigate('/login');
      return;
    }

    if (!selectedBond || units < 1) return;

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/bonds/subscribe`, {
        bond_id: selectedBond.bond_id,
        units: units,
        payment_method: 'stripe'
      }, { 
        headers: { ...headers, 'origin': originUrl },
        withCredentials: true 
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Subscription failed');
    }
  };

  const openSubscribeDialog = (bond) => {
    setSelectedBond(bond);
    setUnits(1);
    setSubscribeOpen(true);
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground font-mono text-sm uppercase tracking-widest">
            {checkingPayment ? 'Verifying Payment...' : 'Loading Bonds...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="bond-marketplace">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-serif text-lg font-semibold text-primary">Royal CBDC</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/public">
                <Button variant="ghost">Public Portal</Button>
              </Link>
              {user ? (
                <Link to={user.role === 'citizen' ? '/citizen' : `/${user.role}`}>
                  <Button data-testid="bond-dashboard-btn">Dashboard</Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button data-testid="bond-login-btn">Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-muted/30 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-5"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1755728806727-d065465340ca?w=1920&q=80')` }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">
              Sovereign Debt Market
            </p>
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-primary tracking-tight mb-6">
              Government<br />
              <span className="font-semibold">Bond Marketplace</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Invest in sovereign bonds backed by the full faith and credit of the nation.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: 'Active Bonds', value: bonds.length, icon: <FileText className="h-5 w-5" /> },
            { label: 'Total Volume', value: '$12.5B', icon: <TrendingUp className="h-5 w-5" /> },
            { label: 'Avg Yield', value: '4.5%', icon: <CreditCard className="h-5 w-5" /> },
            { label: 'Investors', value: '125K+', icon: <CheckCircle className="h-5 w-5" /> }
          ].map((stat, idx) => (
            <Card key={idx} className="dashboard-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">{stat.label}</span>
                  <div className="text-muted-foreground">{stat.icon}</div>
                </div>
                <p className="font-mono text-2xl font-semibold" data-testid={`bond-stat-${idx}`}>{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bond Listings */}
        <div className="mb-8">
          <h2 className="font-serif text-2xl font-normal text-primary mb-6">Available Bonds</h2>
          
          {bonds.length === 0 ? (
            <Card className="sovereign-card">
              <CardContent className="py-16 text-center text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No bonds available</p>
                <p className="text-sm mt-1">Check back soon for new offerings</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bonds.map((bond, idx) => (
                <Card key={idx} className="sovereign-card overflow-hidden" data-testid={`bond-card-${idx}`}>
                  <CardContent className="p-0">
                    <div className="flex flex-col lg:flex-row">
                      {/* Bond Info */}
                      <div className="flex-1 p-6 lg:p-8">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-serif text-xl font-semibold text-primary">{bond.name}</h3>
                            <p className="text-sm text-muted-foreground mt-1">{bond.description}</p>
                          </div>
                          <span className={`px-3 py-1 rounded text-xs font-medium uppercase ${
                            bond.status === 'active' ? 'status-active' : 'status-inactive'
                          }`}>
                            {bond.status}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Face Value</p>
                            <p className="font-mono text-lg font-semibold">${bond.face_value}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Coupon Rate</p>
                            <p className="font-mono text-lg font-semibold text-green-600">{bond.coupon_rate}%</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Maturity</p>
                            <p className="font-mono text-lg font-semibold">{bond.maturity_years} Years</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Available</p>
                            <p className="font-mono text-lg font-semibold">{bond.available_units.toLocaleString()} units</p>
                          </div>
                        </div>
                      </div>

                      {/* Subscribe Section */}
                      <div className="lg:w-64 bg-muted/30 p-6 lg:p-8 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-border">
                        <div className="text-center mb-4">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Min. Investment</p>
                          <p className="font-mono text-2xl font-semibold">${bond.face_value}</p>
                        </div>
                        <Button 
                          className="w-full gap-2" 
                          onClick={() => openSubscribeDialog(bond)}
                          disabled={bond.available_units === 0}
                          data-testid={`subscribe-btn-${idx}`}
                        >
                          <ShoppingCart className="h-4 w-4" />
                          {bond.available_units === 0 ? 'Sold Out' : 'Subscribe'}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          Powered by Stripe
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: <CheckCircle className="h-6 w-6 text-green-500" />,
              title: 'Secure Investment',
              description: 'Government-backed bonds with guaranteed returns'
            },
            {
              icon: <Clock className="h-6 w-6 text-blue-500" />,
              title: 'Regular Payments',
              description: 'Semi-annual coupon payments credited to your wallet'
            },
            {
              icon: <CreditCard className="h-6 w-6 text-purple-500" />,
              title: 'Easy Settlement',
              description: 'Pay with card and receive digital bond certificates'
            }
          ].map((info, idx) => (
            <Card key={idx} className="dashboard-card">
              <CardContent className="p-6 flex gap-4">
                <div className="shrink-0">{info.icon}</div>
                <div>
                  <h3 className="font-semibold mb-1">{info.title}</h3>
                  <p className="text-sm text-muted-foreground">{info.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      {/* Subscribe Dialog */}
      <Dialog open={subscribeOpen} onOpenChange={setSubscribeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Subscribe to Bond</DialogTitle>
          </DialogHeader>
          {selectedBond && (
            <div className="space-y-6 mt-4">
              <div className="p-4 rounded-lg bg-muted/30">
                <h4 className="font-semibold">{selectedBond.name}</h4>
                <p className="text-sm text-muted-foreground mt-1">{selectedBond.description}</p>
              </div>

              <div className="space-y-2">
                <Label>Number of Units</Label>
                <Input
                  type="number"
                  min={1}
                  max={selectedBond.available_units}
                  value={units}
                  onChange={(e) => setUnits(Math.max(1, parseInt(e.target.value) || 1))}
                  data-testid="bond-units-input"
                />
                <p className="text-xs text-muted-foreground">
                  Available: {selectedBond.available_units.toLocaleString()} units
                </p>
              </div>

              <div className="p-4 rounded-lg border border-border space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Price per unit</span>
                  <span className="font-mono">${selectedBond.face_value}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Units</span>
                  <span className="font-mono">× {units}</span>
                </div>
                <div className="border-t border-border pt-2 flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-mono font-semibold text-lg">
                    ${(selectedBond.face_value * units).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 text-sm">
                <AlertCircle className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-muted-foreground">
                  You will be redirected to Stripe for secure payment processing.
                </p>
              </div>

              <Button 
                className="w-full gap-2" 
                onClick={handleSubscribe}
                data-testid="confirm-subscribe-btn"
              >
                Proceed to Payment
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            © 2024 Royal CBDC. Bond investments are subject to market risk.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BondMarketplace;
